import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';

export const DealersTab = ({ dealers, fetchData, dealerDialogOpen, setDealerDialogOpen, userRole, onNavigate }) => {
  const confirm = useConfirm();
  const [formData, setFormData] = useState({ phone_number: '', name: '', town: '', role: 'dealer' });
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [mobileUploading, setMobileUploading] = useState(false);
  const [mobileResult, setMobileResult] = useState(null); // { updated, skipped, errors }
  const [dealerSort, setDealerSort] = useState('name');
  const [dealerSortDir, setDealerSortDir] = useState('asc');

  const toggleDealerSort = (col) => {
    if (dealerSort === col) {
      setDealerSortDir(dealerSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setDealerSort(col);
      setDealerSortDir(col === 'pending' ? 'desc' : 'asc');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await dealerAPI.importExcel(file);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadMobileTemplate = async () => {
    try {
      const res = await dealerAPI.downloadMobileTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `dealer-mobile-template-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUploadMobileUpdate = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMobileUploading(true);
    setMobileResult(null);
    try {
      const res = await dealerAPI.uploadMobileUpdate(file);
      setMobileResult(res.data);
      if (res.data.updated > 0) {
        toast.success(`${res.data.updated} dealer mobile(s) updated`);
      } else if ((res.data.errors || []).length > 0) {
        toast.error('No updates applied — see details');
      } else {
        toast('No changes found in the file');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setMobileUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await dealerAPI.update(editingId, { name: formData.name, phone_number: formData.phone_number, town: formData.town });
        toast.success('Dealer updated');
      } else {
        await dealerAPI.create(formData);
        toast.success('Dealer added');
      }
      setDealerDialogOpen(false);
      setFormData({ phone_number: '', name: '', town: '', role: 'dealer' });
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save dealer');
    }
  };

  const handleEdit = (dealer) => {
    setFormData({ phone_number: dealer.phone_number, name: dealer.name, town: dealer.town || '', role: 'dealer' });
    setEditingId(dealer.id);
    setDealerDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Delete this dealer?',
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    }))) return;
    try {
      await dealerAPI.delete(id);
      toast.success('Dealer deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete dealer');
    }
  };

  // Tokenized AND-match across name + town + phone so "ravi palsana" finds Ravi in Palsana.
  const filtered = dealers.filter((d) => {
    if (!searchQuery) return true;
    const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    const hay = `${d.name || ''} ${d.town || ''} ${d.phone_number || ''}`.toLowerCase();
    return tokens.every(t => hay.includes(t));
  }).sort((a, b) => {
    let valA, valB;
    if (dealerSort === 'name') { valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase(); }
    else if (dealerSort === 'town') { valA = (a.town || '').toLowerCase(); valB = (b.town || '').toLowerCase(); }
    else if (dealerSort === 'pending') { valA = a.pending || 0; valB = b.pending || 0; }
    else { valA = ''; valB = ''; }
    if (dealerSort === 'pending') {
      return dealerSortDir === 'asc' ? valA - valB : valB - valA;
    }
    return dealerSortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  return (
    <div data-testid="dealers-section">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Dealers</h2>
        <div className="flex flex-wrap gap-2">
          {userRole === 'admin' && (<>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium cursor-pointer border" style={{ borderColor: '#E2DFD8', color: '#5C6B61' }} data-testid="import-dealer-btn">
            <Upload className="w-4 h-4" /> {importing ? 'Importing...' : 'Import Excel'}
            <input type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" disabled={importing} data-testid="import-dealer-input" />
          </label>
          <Button
            type="button"
            variant="outline"
            className="inline-flex items-center gap-2"
            onClick={handleDownloadMobileTemplate}
            data-testid="download-mobile-template-btn"
            title="Download an Excel listing all dealers with a 'New Mobile' column to fill"
          >
            <Download className="w-4 h-4" /> Mobile Template
          </Button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium cursor-pointer" style={{ background: '#25D366', color: 'white' }} data-testid="upload-mobile-btn">
            <Upload className="w-4 h-4" /> {mobileUploading ? 'Uploading...' : 'Upload Mobiles'}
            <input type="file" accept=".xlsx,.xls" onChange={handleUploadMobileUpdate} className="hidden" disabled={mobileUploading} data-testid="upload-mobile-input" />
          </label>
        <Dialog open={dealerDialogOpen} onOpenChange={(open) => { setDealerDialogOpen(open); if (!open) { setEditingId(null); setFormData({ phone_number: '', name: '', town: '', role: 'dealer' }); } }}>
          <DialogTrigger asChild>
            <Button style={{ background: '#2B5339' }} data-testid="add-dealer-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Dealer
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="dealer-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Dealer' : 'Add New Dealer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="dealer-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Town / Location</label>
                  <Input value={formData.town} onChange={(e) => setFormData({ ...formData, town: e.target.value })} placeholder="e.g. Jaipur" data-testid="dealer-town-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Mobile Number</label>
                  <Input value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} required placeholder="+91XXXXXXXXXX" data-testid="dealer-phone-input" />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="submit" style={{ background: '#2B5339' }} data-testid="save-dealer-btn">
                  {editingId ? 'Update Dealer' : 'Add Dealer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
          </>)}
        </div>
      </div>

      {/* Mobile update result banner */}
      {mobileResult && (
        <Card className="p-4 mb-4 border" style={{ borderColor: mobileResult.errors?.length ? '#C05C35' : '#2B5339', background: '#FBF9F6' }} data-testid="mobile-update-result">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-semibold text-sm" style={{ color: '#2B5339' }}>
                {mobileResult.updated} updated · {mobileResult.skipped} skipped · {(mobileResult.errors || []).length} error{(mobileResult.errors || []).length !== 1 ? 's' : ''}
              </div>
              {(mobileResult.errors || []).length > 0 && (
                <ul className="mt-2 text-xs space-y-0.5 list-disc pl-5" style={{ color: '#C05C35' }}>
                  {mobileResult.errors.slice(0, 20).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {mobileResult.errors.length > 20 && <li>…and {mobileResult.errors.length - 20} more</li>}
                </ul>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setMobileResult(null)} data-testid="dismiss-mobile-result">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* Search */}
      <Card className="p-4 bg-white border mb-6" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name, town or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
              data-testid="dealer-search-input"
            />
          </div>
          <p className="text-sm" style={{ color: '#5C6B61' }}>{filtered.length} dealer{filtered.length !== 1 ? 's' : ''}</p>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white border overflow-hidden" style={{ borderColor: '#E2DFD8' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: '#FBF9F6' }}>
                <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>S.No</th>
                <th className="text-left p-3 text-xs font-semibold cursor-pointer select-none" style={{ color: dealerSort === 'name' ? '#2B5339' : '#5C6B61' }} onClick={() => toggleDealerSort('name')} data-testid="dealer-sort-name">
                  Name {dealerSort === 'name' ? (dealerSortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th className="text-left p-3 text-xs font-semibold cursor-pointer select-none" style={{ color: dealerSort === 'town' ? '#2B5339' : '#5C6B61' }} onClick={() => toggleDealerSort('town')} data-testid="dealer-sort-town">
                  Town {dealerSort === 'town' ? (dealerSortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Mobile</th>
                <th className="text-center p-3 text-xs font-semibold cursor-pointer select-none" style={{ color: dealerSort === 'pending' ? '#2B5339' : '#5C6B61' }} onClick={() => toggleDealerSort('pending')} data-testid="dealer-sort-pending">
                  Pending {dealerSort === 'pending' ? (dealerSortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Joined</th>
                <th className="text-center p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((dealer, idx) => (
                <tr key={dealer.id} style={{ borderBottom: '1px solid #E2DFD8', background: idx % 2 === 0 ? '#FFFFFF' : '#FBF9F6' }} className="hover:bg-[#F5F1E8] transition-colors cursor-pointer" data-testid="dealer-row" onClick={() => onNavigate({ type: 'dealer', value: dealer.id, label: dealer.name, include_dispatched: true, clear_sched: true })}>
                  <td className="p-3 text-sm">{idx + 1}</td>
                  <td className="p-3 text-sm font-medium" style={{ color: '#2B5339' }}>{dealer.name}</td>
                  <td className="p-3 text-sm" style={{ color: '#5C6B61' }}>{dealer.town || '-'}</td>
                  <td className="p-3 text-sm">{dealer.phone_number}</td>
                  <td className="p-3 text-center">
                    {(dealer.pending || 0) > 0 ? <span className="inline-block px-2 py-0.5 rounded text-xs font-medium" style={{ background: '#E89D4F', color: 'white' }}>{dealer.pending}</span> : <span className="text-xs" style={{ color: '#E2DFD8' }}>-</span>}
                  </td>
                  <td className="p-3 text-xs" style={{ color: '#5C6B61' }}>{new Date(dealer.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                      {userRole === 'admin' && (
                        <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7" onClick={() => handleEdit(dealer)} data-testid="edit-dealer-btn">
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {userRole === 'admin' && (
                        <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-7" onClick={() => handleDelete(dealer.id)} data-testid="delete-dealer-btn">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {userRole !== 'admin' && (
                        <span className="text-xs" style={{ color: '#E2DFD8' }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center" style={{ color: '#5C6B61' }}>No dealers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

